import { useCallback, useMemo, useState } from 'react';
import useFeatures from './useFeatures';
import useSearchParamsConfig from './useSearchParamsConfig';

export default function useExpenseFilters(defaultSearchParams) {
  const features = useFeatures();

  const filters = useMemo(
    () => ({
      createdPeriod: null,
      modifiedPeriod: null,
      expenseApprovalStatuses: [],
      expenseBillableTypeId: null,
      expenseCategories: [],
      expenseClientApprovalStatuses: [],
      expenseInvoiced: null,
      expenseIsReimbursed: null,
      expenseReimbursableTypeId: null,
      expenseTypeId: null,
    }),
    [],
  );

  const [defaultParams] = useState(defaultSearchParams);

  const searchParamsConfig = useSearchParamsConfig();

  const expenseSearchParamsConfig = useMemo(
    () => ({
      createdPeriod: searchParamsConfig.createdPeriod,
      modifiedPeriod: searchParamsConfig.modifiedPeriod,
      expenseApprovalStatuses: searchParamsConfig.expenseApprovalStatuses,
      expenseBillableTypeId: searchParamsConfig.expenseBillableTypeId,
      expenseCategories: searchParamsConfig.expenseCategories,
      expenseClientApprovalStatuses: searchParamsConfig.expenseClientApprovalStatuses,
      expenseInvoiced: searchParamsConfig.expenseInvoiced,
      expenseIsReimbursed: searchParamsConfig.expenseIsReimbursed,
      expenseReimbursableTypeId: searchParamsConfig.expenseReimbursableTypeId,
      expenseTypeId: searchParamsConfig.expenseTypeId,
      ...defaultParams,
    }),
    [searchParamsConfig, defaultParams],
  );

  const mapUrlSearchParams = useCallback(
    (params) => ({
      createdEnd: params.createdPeriod?.end ?? undefined,
      createdStart: params.createdPeriod?.start ?? undefined,
      modifiedEnd: params.modifiedPeriod?.end ?? undefined,
      modifiedStart: params.modifiedPeriod?.start ?? undefined,
      expenseApprovalStatusId: params.expenseApprovalStatuses?.map((v) => v.id),
      expenseBillableTypeId: params.expenseBillableTypeId ?? undefined,
      expenseCategoryId: params.expenseCategories?.map((v) => v.id),
      expenseClientApprovalStatusId: features.clientApprovals
        ? params.expenseClientApprovalStatuses?.map((v) => v.id)
        : undefined,
      expenseInvoiced: params.expenseInvoiced ?? undefined,
      expenseIsReimbursed: params.expenseIsReimbursed ?? undefined,
      expenseReimbursableTypeId: params.expenseReimbursableTypeId ?? undefined,
      expenseTypeId: params.expenseTypeId ?? undefined,
    }),
    [features],
  );

  const mapClickThroughParams = useCallback(
    (filters) => ({
      createdPeriod: filters.createdPeriod ?? undefined,
      modifiedPeriod: filters.modifiedPeriod ?? undefined,
      expenseApprovalStatusId: filters.expenseApprovalStatuses?.map((v) => v.id) ?? undefined,
      expenseBillableTypeId: filters.expenseBillableTypeId ?? undefined,
      expenseCategories: filters.expenseCategories?.map((v) => v.id) ?? undefined,
      expenseClientApprovalStatusId: features.clientApprovals
        ? (filters.expenseClientApprovalStatuses?.map((v) => v.id) ?? undefined)
        : undefined,
      expenseInvoiced: filters.expenseInvoiced ?? undefined,
      expenseIsReimbursed: filters.expenseIsReimbursed ?? undefined,
      expenseReimbursableTypeId: filters.expenseReimbursableTypeId ?? undefined,
      expenseTypeId: filters.expenseTypeId ?? undefined,
    }),
    [features],
  );

  return useMemo(
    () => ({ filters, searchParamsConfig: expenseSearchParamsConfig, mapUrlSearchParams, mapClickThroughParams }),
    [filters, expenseSearchParamsConfig, mapUrlSearchParams, mapClickThroughParams],
  );
}
